import { FC } from 'react';

import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { ResultSearchType } from '@savgroup-front-common/core/molecules/SearchWithDimmer/SearchWithDimmer.types';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@savgroup-front-common/primitive/ui/drawer';
import { RESULT_TYPE } from '@savgroup-front-common/types';
import { SearchResultList } from '@savgroup-front-common/types/SearchResultList';

import { ClearableSearchInputGroup } from '../../../ClearableSearchInputGroup/ClearableSearchInputGroup';
import { AppBarSearchResultsList } from '../AppBarSearchPanel/AppBarSearchResultsList';

import messages from './messages';

interface AppBarSearchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  query?: string;
  isLoading: boolean;
  isFetchingMore: boolean;
  focusedResultType?: RESULT_TYPE;
  results: SearchResultList<ResultSearchType>[];
  onSearch: (value: string) => void;
  onShowMore: (resultType: RESULT_TYPE) => void;
}

export const AppBarSearchDrawer: FC<AppBarSearchDrawerProps> = ({
  isOpen,
  onClose,
  query = '',
  isLoading,
  isFetchingMore,
  focusedResultType,
  results,
  onSearch,
  onShowMore,
}) => {
  return (
    <Drawer
      open={isOpen}
      onOpenChange={(isDrawerOpen) => {
        if (!isDrawerOpen) {
          onClose();
        }
      }}
      repositionInputs={false}
    >
      <DrawerContent data-testid="appBar_searchDrawer" className="max-h-[85vh]">
        <DrawerHeader className="sr-only">
          <DrawerTitle>
            {safeFormattedIntlString(messages.searchTitle)}
          </DrawerTitle>
        </DrawerHeader>
        <div className="shrink-0 px-4 py-4">
          <ClearableSearchInputGroup
            value={query}
            onChange={onSearch}
            placeholder={safeFormattedIntlString(messages.searchPlaceholder)}
            clearAriaLabel={safeFormattedIntlString(messages.clearSearch)}
            isLoading={isLoading}
            autoFocus
            inputDataTestId="appBarSearchDrawerInput"
            clearDataTestId="appBarSearchDrawerClear"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <AppBarSearchResultsList
            query={query}
            isLoading={isLoading}
            isFetchingMore={isFetchingMore}
            focusedResultType={focusedResultType}
            results={results}
            onShowMore={onShowMore}
            onSelectResult={onClose}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
